'use client';

import { ButtonAction } from '@heidi/analytics/vocabulary';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type ReactNode, useEffect, useState } from 'react';

import {
  type JoinedWorkspaceRelation,
  type WorkspaceInvitationView,
  type WorkspaceShellButtonAction,
} from '../../contracts';
import { trackWorkspaceShellEvent } from '../../data/analytics/track-workspace-shell-event';
import { acceptTeamInvitation } from '../../data/api/teams';
import { setWorkspaceId } from '../../data/atoms/workspaceAtoms';
import { useQueryWorkspaceInvitations } from '../../data/query/useQueryWorkspaceInvitations';
import {
  useWorkspaceHydrationStatus,
  type WorkspaceHydrationStatus,
} from '../../data/query/useWorkspaceHydrationStatus';
import { useIsWorkspaceSwitcherEnabled } from '../../flags';
import {
  WorkspaceSelector,
  type WorkspaceSelectorProps,
} from '../workspace-selector/workspace-selector';
import { WorkspaceSwitchLoader } from '../workspace-switch/workspace-switch-loader';
import { type WorkspaceHydrationGatePorts } from './ports';
import { WorkspaceHydrationError } from './workspace-hydration-error';

/**
 * The selector plus what only shows once it does: the pending-invitation rows.
 * Mounted ONLY for the `awaiting_selection` state, so `GET /workspaces/invitations`
 * fires for a multi-workspace login and not on every cold boot.
 *
 * Joining from here mirrors the sidebar's `JoinWorkspaceDialog`: accept, persist
 * the invitation's workspace, then a full page load on /scribe so every
 * workspace-scoped cache boots under it (the persisted id is what keeps the
 * selector from showing again). The dialog itself cannot mount above
 * `UserResolver` — it reads the user query — which is why the accept is
 * composed here rather than reused.
 *
 * Picking a workspace is not the only way off this surface: the selector's
 * Sign out utility runs through `onSignOutStart` and `ports.signOut`.
 */
function WorkspaceSelection({
  workspaces,
  onSelect,
  onSignOutStart,
  signOut,
}: {
  workspaces: JoinedWorkspaceRelation[];
  onSelect: (workspaceId: string) => void;
  onSignOutStart: () => void;
  signOut: WorkspaceHydrationGatePorts['signOut'];
}) {
  const { data: invitationsData } = useQueryWorkspaceInvitations();
  const [joinState, setJoinState] = useState<WorkspaceSelectorProps['joinState']>(null);

  // Every emit here is a NEW dispatch site; the app binding opts each out of
  // Amplitude (Snowplow-first policy).
  const trackSelectorButton = (action: WorkspaceShellButtonAction, label: string) => {
    trackWorkspaceShellEvent({
      event_name: 'button_clicked',
      props: { screen_name: 'workspace_selector', action, label },
    });
  };

  // One impression per selector mount.
  useEffect(() => {
    trackWorkspaceShellEvent({
      event_name: 'screen_viewed',
      props: { screen_name: 'workspace_selector', entry_point: 'login' },
    });
  }, []);

  const selectWorkspace = (workspaceId: string) => {
    trackSelectorButton(ButtonAction.WorkspaceSelectorWorkspaceSelected, 'workspace');
    onSelect(workspaceId);
  };

  const joinInvitation = async (invitation: WorkspaceInvitationView) => {
    trackSelectorButton(ButtonAction.WorkspaceSelectorJoinTeam, 'join_team');
    setJoinState({ invitationId: invitation.invitation_id, status: 'joining' });
    try {
      await acceptTeamInvitation(invitation.team_id);
      if (invitation.workspace_id) {
        setWorkspaceId(invitation.workspace_id);
      }
      window.location.assign('/scribe');
    } catch (error) {
      // Classification only: an invitation id is not PHI, but the error body may
      // echo the account email (INC-345), so it never reaches the log.
      logger.warn(
        {
          event: 'workspace_selector_join_failed',
          ...sanitizeError(error),
        },
        'Workspace selector: accepting an invitation failed'
      );
      setJoinState({ invitationId: invitation.invitation_id, status: 'failed' });
    }
  };

  // Keep the gate blocked while sign-out clears the token and redirects.
  // Reload if sign-out fails.
  const handleSignOut = () => {
    onSignOutStart();
    trackSelectorButton(ButtonAction.WorkspaceSelectorSignOut, 'sign_out');
    signOut().catch((error: unknown) => {
      logger.warn(
        {
          event: 'workspace_selector_sign_out_failed',
          ...sanitizeError(error),
        },
        'Workspace selector: signing out failed'
      );
      window.location.reload();
    });
  };

  return (
    <WorkspaceSelector
      workspaces={workspaces}
      onSelect={selectWorkspace}
      invitations={invitationsData?.invitations}
      onJoin={(invitation) => void joinInvitation(invitation)}
      joinState={joinState}
      onSignOut={handleSignOut}
    />
  );
}

/**
 * Post-authentication chokepoint: blocks the authenticated app (which fires
 * `Heidi-Workspace-Id`-tagged requests as soon as the token lands) until the
 * active workspace is resolved. Sits between `<AuthTokenResolver>` and
 * `<UserResolver>` in `AuthWrapper` (web) and wraps `<TauriWrapper>` on
 * desktop; `FhirAuth` is intentionally exempt. Blocking/fail-open policy lives
 * in {@link useWorkspaceHydrationStatus}.
 *
 * Also hosts the workspace switch loader: this is the first thing a reloaded
 * document mounts once the token is back, above `UserResolver` on both
 * platforms, so it is where the overlay can cover the boot into the workspace
 * the user switched to.
 */
export function WorkspaceHydrationGate({
  children,
  ports,
}: {
  children: ReactNode;
  /** The loader, the PostHog sync and sign-out, bound app-side. */
  ports: WorkspaceHydrationGatePorts;
}) {
  const status = useWorkspaceHydrationStatus();
  ports.useSyncHydrationStatus(status);
  const isSwitcherEnabled = useIsWorkspaceSwitcherEnabled();
  // One-way: once sign-out starts, the gate shows the loader for the rest of
  // this mount, since releasing on token/atom state mid-teardown is briefly inconsistent.
  const [isSigningOut, setIsSigningOut] = useState(false);

  return (
    <>
      {isSwitcherEnabled ? <WorkspaceSwitchLoader /> : null}
      {isSigningOut ? (
        ports.pendingFallback
      ) : (
        <GateContent
          status={status}
          ports={ports}
          onSignOutStart={() => setIsSigningOut(true)}
        >
          {children}
        </GateContent>
      )}
    </>
  );
}

function GateContent({
  status,
  ports,
  onSignOutStart,
  children,
}: {
  status: WorkspaceHydrationStatus;
  ports: WorkspaceHydrationGatePorts;
  onSignOutStart: () => void;
  children: ReactNode;
}) {
  if (status.state === 'pending') {
    return <>{ports.pendingFallback}</>;
  }

  if (status.state === 'awaiting_selection') {
    // a fresh login into 2+ workspaces. The surface lives behind
    // `@heidi/workspace`; the decision to show it, and what selecting means
    // (persist, which releases this gate), stay in the status hook.
    return (
      <WorkspaceSelection
        workspaces={status.workspaces}
        onSelect={status.select}
        onSignOutStart={onSignOutStart}
        signOut={ports.signOut}
      />
    );
  }

  if (
    status.state === 'degraded' &&
    (status.reason === 'user_not_provisioned' || status.reason === 'session_expired')
  ) {
    // Fail-open: the signup form (400) and the login surface (settled 401)
    // live BELOW the gate — blocking would cover them. Rationale in the hook.
    return <>{children}</>;
  }

  if (status.state === 'degraded') {
    // The panel itself lives behind `@heidi/workspace`; what stays here is the
    // decision to show it. Retrying is this file's to define, so it crosses as
    // a callback rather than the panel growing its own refetch.
    return <WorkspaceHydrationError onRetry={status.retry} />;
  }

  return <>{children}</>;
}
